/**
 * Signing in as somebody without signing in at all.
 *
 * When the backend runs with `AUTH_ENABLED=false` there is no Microsoft
 * sign-in, and there used to be exactly one local identity - which was root, so
 * the app could only ever be seen from the most privileged seat there is. Now
 * the backend offers a handful of personas (`backend/dev_users.py`) and this
 * module picks which one the browser is, sending it as `X-Dev-User` on every
 * request.
 *
 * **The backend is asked, never told.** `GET /api/auth-config` is what decides
 * whether sign-in is on, so the two halves cannot disagree - there is no second
 * `VITE_` flag to forget. In any deployed environment that endpoint reports
 * authentication on and an empty persona list, and every function here becomes
 * inert.
 *
 * The choice lives in `localStorage`, so it survives a reload; switching writes
 * it and reloads the page, which is the cheap way to be certain nothing is left
 * over from the previous persona - no cached charts, no stale permissions.
 */

import type { AuthenticatedUser } from './profile.ts';

export interface DevPersona {
  key: string;
  name: string;
  email: string;
  /** `root`, a role name, or `none` - what the seed script gave them. */
  role: string;
  summary: string;
}

export interface AuthConfig {
  auth_enabled: boolean;
  personas: DevPersona[];
  default_persona: string | null;
}

const STORAGE_KEY = 'neptune_dev_user';

/** Assume sign-in is required until told otherwise - the safe default. */
const AUTH_REQUIRED: AuthConfig = { auth_enabled: true, personas: [], default_persona: null };

let config: AuthConfig = AUTH_REQUIRED;
let loaded: Promise<AuthConfig> | null = null;

async function fetchConfig(): Promise<AuthConfig> {
  try {
    const resp = await fetch('/api/auth-config', { headers: { Accept: 'application/json' } });
    if (!resp.ok) return AUTH_REQUIRED;
    const body = (await resp.json()) as Partial<AuthConfig>;
    config = {
      auth_enabled: body.auth_enabled !== false,
      personas: body.personas ?? [],
      default_persona: body.default_persona ?? null,
    };
    return config;
  } catch {
    // A backend that cannot be reached is not a backend that has authentication
    // switched off. Keep requiring sign-in.
    return AUTH_REQUIRED;
  }
}

/** Ask the backend how sign-in works here. Cached for the life of the page. */
export function loadAuthConfig(): Promise<AuthConfig> {
  loaded ??= fetchConfig();
  return loaded;
}

/** Whether this page is running as a development persona rather than an Entra account. */
export function isDevIdentity(): boolean {
  return !config.auth_enabled;
}

export function devPersonas(): DevPersona[] {
  return config.personas;
}

/** The persona this browser has chosen, or the backend's default. */
export function currentPersonaKey(): string {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  if (stored && config.personas.some((p) => p.key === stored)) return stored;
  return config.default_persona ?? config.personas[0]?.key ?? 'root';
}

export function currentPersona(): DevPersona | null {
  const key = currentPersonaKey();
  return config.personas.find((p) => p.key === key) ?? null;
}

/** Become somebody else. Reloads, so nothing of the old persona survives. */
export function setPersona(key: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // A browser that refuses storage still gets the switch for this page load.
  }
  window.location.reload();
}

/** The header every request carries while a persona is active. */
export function devIdentityHeader(): Record<string, string> {
  return isDevIdentity() ? { 'X-Dev-User': currentPersonaKey() } : {};
}

/** The chosen persona in the shape the header and `authState` expect. */
export function devUser(): AuthenticatedUser {
  const persona = currentPersona();
  return {
    oid: `dev:${persona?.key ?? 'root'}`,
    tid: 'development',
    name: persona?.name ?? 'Local developer',
    email: persona?.email ?? 'local@development.invalid',
    username: persona?.email ?? 'local@development.invalid',
  };
}
